import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, Search, Globe, FileText, Tag, Image as ImageIcon, AlertCircle, Users, Settings, Database, LayoutGrid as Layout, CreditCard as Edit3, Check, X, Info, Building, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import SEOHead from '../components/SEOHead';

interface SeoSettings {
  [key: string]: {
    title: string;
    description: string;
    keywords: string;
    image?: string;
  };
}

const AdminPanel = () => {
  const { user, isAdmin } = useAuth();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('seo');
  const [activePage, setActivePage] = useState('home');
  const [saving, setSaving] = useState(false);
  const [seoSettings, setSeoSettings] = useState<SeoSettings>({
    home: {
      title: 'Grad Vikalp - Find Your Dream College | College Guidance Platform',
      description: 'Find your dream college with Grad Vikalp! Get verified student reviews, personalized college recommendations, and connect with real students. Your trusted partner for JEE, NEET, and college admissions guidance.',
      keywords: 'college admission, JEE colleges, NEET colleges, college reviews, personalized college list, engineering colleges, medical colleges, college guidance, student reviews, college comparison, admission counseling',
      image: '/Grad Vikalp (3).png'
    },
    about: {
      title: 'About Grad Vikalp - Your Trusted College Guidance Partner',
      description: 'Learn about Grad Vikalp\'s mission to democratize college selection through verified information, personalized guidance, and real student connections. Discover our story, values, and commitment to student success.',
      keywords: 'about grad vikalp, college guidance platform, education counseling, student success, college selection help, verified college reviews',
      image: '/Grad-Vikalp.png'
    },
    blog: {
      title: 'College Guidance Blog - Expert Tips & Admission Insights | Grad Vikalp',
      description: 'Get expert college guidance, admission tips, JEE & NEET preparation strategies, and career advice from our education experts. Stay updated with latest college admission trends and insights.',
      keywords: 'college guidance blog, admission tips, JEE preparation, NEET guidance, college admission advice, career counseling, education blog, college selection tips',
      image: '/Grad-Vikalp.png'
    },
    'college-reviews': {
      title: 'College Reviews - Verified Student Reviews | Grad Vikalp',
      description: 'Read authentic college reviews from real students. Get insights about engineering, medical, and other colleges across India. Make informed decisions with verified student experiences and ratings.',
      keywords: 'college reviews, student reviews, college ratings, engineering college reviews, medical college reviews, authentic college feedback, college experiences, verified reviews',
      image: '/Grad-Vikalp.png'
    },
    'personalized-list': {
      title: 'NEET College Predictor - AI-Powered Medical College Recommendations | Grad Vikalp',
      description: 'Get personalized medical college predictions based on your NEET rank, category, and preferences. AI-powered recommendations with admission probability analysis for just ₹299. Instant results with detailed college information.',
      keywords: 'NEET college predictor, NEET college recommendations, medical college list, NEET admission probability, rank-based medical colleges, AI medical college recommendations, NEET college selection tool',
      image: '/Grad-Vikalp.png'
    },
    contact: {
      title: 'Contact Grad Vikalp - Get College Guidance & Support',
      description: 'Contact Grad Vikalp for college guidance, admission counseling, and support. Reach out via email, phone, or our contact form. We\'re here to help with your college selection journey.',
      keywords: 'contact grad vikalp, college guidance support, admission counseling, education help, college selection assistance, student support',
      image: '/Grad-Vikalp.png'
    }
  });

  // Load settings from localStorage on component mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('gradVikalpSeoSettings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        setSeoSettings(parsedSettings);
      } catch (error) {
        console.error('Error parsing saved SEO settings:', error);
      }
    }
  }, []);

  // Get user role on component mount
  useEffect(() => {
    if (user) {
      setUserRole(user.role);
    }
  }, [user]);

  const handleInputChange = (field: string, value: string) => {
    setSeoSettings(prev => ({
      ...prev,
      [activePage]: {
        ...prev[activePage],
        [field]: value
      }
    }));
  };

  const saveSettings = () => {
    setSaving(true);
    
    try {
      // Save to localStorage
      localStorage.setItem('gradVikalpSeoSettings', JSON.stringify(seoSettings));
      
      // In a real application, you would save to a database here
      // For now, we'll just simulate a delay
      setTimeout(() => {
        toast.success('SEO settings saved successfully!');
        setSaving(false);
      }, 800);
    } catch (error) {
      console.error('Error saving SEO settings:', error);
      toast.error('Failed to save SEO settings');
      setSaving(false);
    }
  };

  const resetPageSettings = () => {
    if (!confirm('Are you sure you want to reset the SEO settings for this page?')) return;
    
    const defaultSettings = {
      home: {
        title: 'Grad Vikalp - Find Your Dream College | College Guidance Platform',
        description: 'Find your dream college with Grad Vikalp! Get verified student reviews, personalized college recommendations, and connect with real students. Your trusted partner for JEE, NEET, and college admissions guidance.',
        keywords: 'college admission, JEE colleges, NEET colleges, college reviews, personalized college list, engineering colleges, medical colleges, college guidance, student reviews, college comparison, admission counseling',
        image: '/Grad-Vikalp.png'
      },
      about: {
        title: 'About Grad Vikalp - Your Trusted College Guidance Partner',
        description: 'Learn about Grad Vikalp\'s mission to democratize college selection through verified information, personalized guidance, and real student connections. Discover our story, values, and commitment to student success.',
        keywords: 'about grad vikalp, college guidance platform, education counseling, student success, college selection help, verified college reviews',
        image: '/Grad-Vikalp.png'
      },
      blog: {
        title: 'College Guidance Blog - Expert Tips & Admission Insights | Grad Vikalp',
        description: 'Get expert college guidance, admission tips, JEE & NEET preparation strategies, and career advice from our education experts. Stay updated with latest college admission trends and insights.',
        keywords: 'college guidance blog, admission tips, JEE preparation, NEET guidance, college admission advice, career counseling, education blog, college selection tips',
        image: '/Grad-Vikalp.png'
      },
      'college-reviews': {
        title: 'College Reviews - Verified Student Reviews | Grad Vikalp',
        description: 'Read authentic college reviews from real students. Get insights about engineering, medical, and other colleges across India. Make informed decisions with verified student experiences and ratings.',
        keywords: 'college reviews, student reviews, college ratings, engineering college reviews, medical college reviews, authentic college feedback, college experiences, verified reviews',
        image: '/Grad-Vikalp.png'
      },
      'personalized-list': {
        title: 'NEET College Predictor - AI-Powered Medical College Recommendations | Grad Vikalp',
        description: 'Get personalized medical college predictions based on your NEET rank, category, and preferences. AI-powered recommendations with admission probability analysis for just ₹299. Instant results with detailed college information.',
        keywords: 'NEET college predictor, NEET college recommendations, medical college list, NEET admission probability, rank-based medical colleges, AI medical college recommendations, NEET college selection tool',
        image: '/Grad-Vikalp.png'
      },
      contact: {
        title: 'Contact Grad Vikalp - Get College Guidance & Support',
        description: 'Contact Grad Vikalp for college guidance, admission counseling, and support. Reach out via email, phone, or our contact form. We\'re here to help with your college selection journey.',
        keywords: 'contact grad vikalp, college guidance support, admission counseling, education help, college selection assistance, student support',
        image: '/Grad-Vikalp.png'
      }
    };
    
    setSeoSettings(prev => ({
      ...prev,
      [activePage]: defaultSettings[activePage as keyof typeof defaultSettings]
    }));
    
    toast.success('Page SEO settings reset to default');
  };

  const resetAllSettings = () => {
    if (!confirm('Are you sure you want to reset ALL SEO settings to default? This cannot be undone.')) return;
    
    const defaultSettings = {
      home: {
        title: 'Grad Vikalp - Find Your Dream College | College Guidance Platform',
        description: 'Find your dream college with Grad Vikalp! Get verified student reviews, personalized college recommendations, and connect with real students. Your trusted partner for JEE, NEET, and college admissions guidance.',
        keywords: 'college admission, JEE colleges, NEET colleges, college reviews, personalized college list, engineering colleges, medical colleges, college guidance, student reviews, college comparison, admission counseling',
        image: '/Grad-Vikalp.png'
      },
      about: {
        title: 'About Grad Vikalp - Your Trusted College Guidance Partner',
        description: 'Learn about Grad Vikalp\'s mission to democratize college selection through verified information, personalized guidance, and real student connections. Discover our story, values, and commitment to student success.',
        keywords: 'about grad vikalp, college guidance platform, education counseling, student success, college selection help, verified college reviews',
        image: '/Grad-Vikalp.png'
      },
      blog: {
        title: 'College Guidance Blog - Expert Tips & Admission Insights | Grad Vikalp',
        description: 'Get expert college guidance, admission tips, JEE & NEET preparation strategies, and career advice from our education experts. Stay updated with latest college admission trends and insights.',
        keywords: 'college guidance blog, admission tips, JEE preparation, NEET guidance, college admission advice, career counseling, education blog, college selection tips',
        image: '/Grad-Vikalp.png'
      },
      'college-reviews': {
        title: 'College Reviews - Verified Student Reviews | Grad Vikalp',
        description: 'Read authentic college reviews from real students. Get insights about engineering, medical, and other colleges across India. Make informed decisions with verified student experiences and ratings.',
        keywords: 'college reviews, student reviews, college ratings, engineering college reviews, medical college reviews, authentic college feedback, college experiences, verified reviews',
        image: '/Grad-Vikalp.png'
      },
      'personalized-list': {
        title: 'NEET College Predictor - AI-Powered Medical College Recommendations | Grad Vikalp',
        description: 'Get personalized medical college predictions based on your NEET rank, category, and preferences. AI-powered recommendations with admission probability analysis for just ₹299. Instant results with detailed college information.',
        keywords: 'NEET college predictor, NEET college recommendations, medical college list, NEET admission probability, rank-based medical colleges, AI medical college recommendations, NEET college selection tool',
        image: '/Grad-Vikalp.png'
      },
      contact: {
        title: 'Contact Grad Vikalp - Get College Guidance & Support',
        description: 'Contact Grad Vikalp for college guidance, admission counseling, and support. Reach out via email, phone, or our contact form. We\'re here to help with your college selection journey.',
        keywords: 'contact grad vikalp, college guidance support, admission counseling, education help, college selection assistance, student support',
        image: '/Grad-Vikalp.png'
      }
    };
    
    setSeoSettings(defaultSettings);
    localStorage.setItem('gradVikalpSeoSettings', JSON.stringify(defaultSettings));
    toast.success('All SEO settings reset to default');
  };

  // Check if user is admin (not counselor)
  const isAdminOnly = userRole === 'admin';

  // Redirect if not admin or counselor
  if (!isAdmin && userRole !== 'admin' && userRole !== 'counselor') {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <AlertCircle className="text-warning mx-auto mb-3" size={64} />
                </div>
                <h1 className="h2 text-dark mb-3">Access Restricted</h1>
                <p className="text-muted mb-4">
                  You need admin privileges to access the Admin Panel.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  <Link to="/" className="btn btn-primary">
                    Back to Home
                  </Link>
                  <Link to="/contact" className="btn btn-outline-primary">
                    Contact Support
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
      <SEOHead
        title="Admin Panel - Grad Vikalp"
        description="Admin panel for managing Grad Vikalp website settings and content."
        keywords="admin, management, settings, SEO"
        url="/admin"
        type="website"
      />
      <div className="container py-4">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-primary rounded-3 p-3">
              <Settings className="text-white" size={32} />
            </div>
            <div>
              <h1 className="h2 fw-bold text-dark mb-1">Admin Panel</h1>
              <p className="text-muted mb-0">Manage website settings and content</p>
            </div>
          </div>
          <Link to="/" className="btn btn-outline-secondary">
            Back to Home
          </Link>
        </div>

        {/* Admin Tabs */}
        <div className="row g-4">
          <div className="col-lg-3">
            <div className="card border-0 shadow-lg">
              <div className="card-body p-0">
                <div className="list-group list-group-flush">
                  <button
                    onClick={() => setActiveTab('seo')}
                    className={`list-group-item list-group-item-action d-flex align-items-center gap-3 ${activeTab === 'seo' ? 'active' : ''} ${!isAdminOnly ? 'disabled text-muted' : ''}`}
                    disabled={!isAdminOnly}
                  >
                    <Globe size={20} />
                    <span>SEO Settings {!isAdminOnly && <small>(Admin Only)</small>}</span>
                  </button>
                  <Link
                    to="/manage-colleges"
                    className="list-group-item list-group-item-action d-flex align-items-center gap-3"
                  >
                    <Database size={20} />
                    <span>College Manager</span>
                  </Link>
                  <Link
                    to="/manage-personalized-colleges"
                    className="list-group-item list-group-item-action d-flex align-items-center gap-3"
                  >
                    <Layout size={20} />
                    <span>Personalized Colleges</span>
                  </Link>
                  <Link
                    to="/admin/personalized-lists" 
                    className="list-group-item list-group-item-action d-flex align-items-center gap-3"
                  >
                    <Users size={20} />
                    <span>NEET Submissions</span>
                  </Link>
                  <Link
                    to="/admin/blog-manager"
                    className="list-group-item list-group-item-action d-flex align-items-center gap-3"
                  >
                    <FileText size={20} />
                    <span>Blog Manager</span>
                  </Link>
                  <Link
                    to="/admin/college-manager"
                    className="list-group-item list-group-item-action d-flex align-items-center gap-3"
                  >
                    <Building size={20} />
                    <span>College Manager</span>
                  </Link>
                  <Link
                    to="/admin/seo-manager"
                    className="list-group-item list-group-item-action d-flex align-items-center gap-3"
                  >
                    <Globe size={20} />
                    <span>SEO Manager</span>
                  </Link>
                  <Link
                    to="/admin/settings"
                    className="list-group-item list-group-item-action d-flex align-items-center gap-3 fw-semibold"
                  >
                    <Shield size={20} />
                    <span>Promo Codes &amp; Credentials</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-9">
            {/* SEO Settings */}
            {activeTab === 'seo' && isAdminOnly && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card border-0 shadow-lg"
              >
                <div className="card-header bg-white p-4 border-bottom">
                  <div className="d-flex justify-content-between align-items-center">
                    <h3 className="h5 fw-bold text-dark mb-0">SEO Settings</h3>
                    <div className="d-flex gap-2">
                      <button
                        onClick={resetPageSettings}
                        className="btn btn-outline-warning btn-sm d-flex align-items-center gap-2"
                      >
                        <X size={14} />
                        Reset Page
                      </button>
                      <button
                        onClick={resetAllSettings}
                        className="btn btn-outline-danger btn-sm d-flex align-items-center gap-2"
                      >
                        <AlertCircle size={14} />
                        Reset All
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="card-body p-4">
                  <div className="row g-3 mb-4">
                    <div className="col-md-3">
                      <div className="list-group">
                        <button
                          onClick={() => setActivePage('home')}
                          className={`list-group-item list-group-item-action d-flex align-items-center gap-2 ${activePage === 'home' ? 'active' : ''}`}
                        >
                          <span>Home Page</span>
                        </button>
                        <button
                          onClick={() => setActivePage('about')}
                          className={`list-group-item list-group-item-action d-flex align-items-center gap-2 ${activePage === 'about' ? 'active' : ''}`}
                        >
                          <span>About Page</span>
                        </button>
                        <button
                          onClick={() => setActivePage('blog')}
                          className={`list-group-item list-group-item-action d-flex align-items-center gap-2 ${activePage === 'blog' ? 'active' : ''}`}
                        >
                          <span>Blog Page</span>
                        </button>
                        <button
                          onClick={() => setActivePage('college-reviews')}
                          className={`list-group-item list-group-item-action d-flex align-items-center gap-2 ${activePage === 'college-reviews' ? 'active' : ''}`}
                        >
                          <span>College Reviews</span>
                        </button>
                        <button
                          onClick={() => setActivePage('neet-college-predictor')}
                          className={`list-group-item list-group-item-action d-flex align-items-center gap-2 ${activePage === 'neet-college-predictor' ? 'active' : ''}`}
                        >
                          <span>NEET Predictor</span>
                        </button>
                        <button
                          onClick={() => setActivePage('contact')}
                          className={`list-group-item list-group-item-action d-flex align-items-center gap-2 ${activePage === 'contact' ? 'active' : ''}`}
                        >
                          <span>Contact Page</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="col-md-9">
                      <div className="card border">
                        <div className="card-header bg-light">
                          <h4 className="h6 fw-bold mb-0">
                            Editing SEO for: {activePage.charAt(0).toUpperCase() + activePage.slice(1).replace('-', ' ')} Page
                          </h4>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <label className="form-label fw-semibold d-flex align-items-center gap-2">
                              <FileText size={16} />
                              Page Title
                            </label>
                            <input
                              type="text"
                              value={seoSettings[activePage]?.title || ''}
                              onChange={(e) => handleInputChange('title', e.target.value)}
                              className="form-control"
                              placeholder="Enter page title"
                            />
                            <div className="form-text">
                              Recommended length: 50-60 characters. Current: {seoSettings[activePage]?.title.length || 0} characters
                            </div>
                          </div>
                          
                          <div className="mb-3">
                            <label className="form-label fw-semibold d-flex align-items-center gap-2">
                              <FileText size={16} />
                              Meta Description
                            </label>
                            <textarea
                              value={seoSettings[activePage]?.description || ''}
                              onChange={(e) => handleInputChange('description', e.target.value)}
                              className="form-control"
                              rows={3}
                              placeholder="Enter meta description"
                            />
                            <div className="form-text">
                              Recommended length: 150-160 characters. Current: {seoSettings[activePage]?.description.length || 0} characters
                            </div>
                          </div>
                          
                          <div className="mb-3">
                            <label className="form-label fw-semibold d-flex align-items-center gap-2">
                              <Tag size={16} />
                              Meta Keywords
                            </label>
                            <textarea
                              value={seoSettings[activePage]?.keywords || ''}
                              onChange={(e) => handleInputChange('keywords', e.target.value)}
                              className="form-control"
                              rows={2}
                              placeholder="Enter meta keywords (comma-separated)"
                            />
                            <div className="form-text">
                              Separate keywords with commas. Recommended: 5-10 relevant keywords
                            </div>
                          </div>
                          
                          <div className="mb-3">
                            <label className="form-label fw-semibold d-flex align-items-center gap-2">
                              <ImageIcon size={16} />
                              Social Image URL
                            </label>
                            <input
                              type="text"
                              value={seoSettings[activePage]?.image || ''}
                              onChange={(e) => handleInputChange('image', e.target.value)}
                              className="form-control"
                              placeholder="Enter image URL for social sharing"
                            />
                            <div className="form-text">
                              Recommended size: 1200x630 pixels for optimal social sharing
                            </div>
                          </div>
                          
                          <div className="alert alert-info d-flex align-items-start gap-2">
                            <Info size={18} className="flex-shrink-0 mt-1" />
                            <div>
                              <strong>How this works:</strong> These settings are saved to your browser's local storage. 
                              When you visit the respective pages, the SEO component will read these settings and apply them.
                              <br /><br />
                              <strong>Note:</strong> In a production environment, these settings would typically be saved to a database.
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <button
                          onClick={saveSettings}
                          disabled={saving}
                          className="btn btn-primary d-flex align-items-center gap-2"
                        >
                          {saving ? (
                            <>
                              <span className="spinner-border spinner-border-sm" role="status"></span>
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save size={16} />
                              Save SEO Settings
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card border mt-4">
                    <div className="card-header bg-light">
                      <h4 className="h6 fw-bold mb-0">SEO Preview</h4>
                    </div>
                    <div className="card-body">
                      <div className="mb-4">
                        <h5 className="h6 fw-semibold text-dark mb-2">Google Search Result Preview</h5>
                        <div className="border p-3 rounded bg-white">
                          <div style={{ fontFamily: 'Arial, sans-serif' }}>
                            <div style={{ color: '#1a0dab', fontSize: '18px', marginBottom: '4px', fontWeight: 'normal' }}>
                              {seoSettings[activePage]?.title || 'Page Title'}
                            </div>
                            <div style={{ color: '#006621', fontSize: '14px', marginBottom: '4px' }}>
                              https://gradvikalp.com/{activePage === 'home' ? '' : activePage}
                            </div>
                            <div style={{ color: '#545454', fontSize: '14px', lineHeight: '1.4' }}>
                              {seoSettings[activePage]?.description || 'Meta description will appear here...'}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h5 className="h6 fw-semibold text-dark mb-2">Social Media Preview</h5>
                        <div className="border p-3 rounded bg-white">
                          <div style={{ maxWidth: '500px' }}>
                            <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                              <div style={{ height: '250px', background: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {seoSettings[activePage]?.image ? (
                                  <img 
                                    src={seoSettings[activePage]?.image} 
                                    alt="Social preview" 
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} 
                                  />
                                ) : (
                                  <div style={{ color: '#aaa', textAlign: 'center' }}>
                                    <ImageIcon size={48} />
                                    <div>Image Preview</div>
                                  </div>
                                )}
                              </div>
                              <div style={{ padding: '12px' }}>
                                <div style={{ color: '#385898', fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
                                  {seoSettings[activePage]?.title || 'Page Title'}
                                </div>
                                <div style={{ color: '#606770', fontSize: '14px', marginBottom: '4px' }}>
                                  gradvikalp.com
                                </div>
                                <div style={{ color: '#606770', fontSize: '14px', lineHeight: '1.4' }}>
                                  {seoSettings[activePage]?.description || 'Meta description will appear here...'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            
            {/* Access Denied Message */}
            {activeTab === 'seo' && !isAdminOnly && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card border-0 shadow-lg"
              >
                <div className="card-body p-5 text-center">
                  <AlertCircle size={64} className="text-warning mb-3" />
                  <h3 className="h4 text-dark mb-3">Access Restricted</h3>
                  <p className="text-muted mb-4">
                    SEO settings can only be accessed by administrators. Counselors do not have permission to modify SEO settings.
                  </p>
                  <button
                    onClick={() => setActiveTab('seo')}
                    className="btn btn-primary"
                  >
                    Back to Admin Panel
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;